import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  Copy,
  Loader2,
  Monitor,
  Plus,
  Printer as PrinterIcon,
  RefreshCw,
  Trash2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/api/client';

/**
 * Printing without a print dialog.
 *
 * Two things get set up here, and the distinction matters because getting it
 * wrong produces a printer that accepts jobs and never prints:
 *
 *   An **agent** is the PC in the shop that does the printing. ShipSwift runs
 *   on Railway and cannot reach anything on the shop's network, so the shop
 *   side has to reach out. No agent running means no direct printing — the
 *   browser print dialog still works from anywhere.
 *
 *   A **printer** is where a label physically comes out, and belongs to an
 *   agent. A network Zebra is addressed by IP and takes ZPL; anything else is
 *   addressed by its Windows name and takes PDFs.
 */

const EMPTY_PRINTER = {
  name: '',
  kind: 'WINDOWS_DRIVER',
  host: '',
  port: 9100,
  windowsName: '',
  agentId: '',
  isDefault: false,
};

export default function PrintingSection() {
  const queryClient = useQueryClient();
  const [newAgentName, setNewAgentName] = useState('');
  const [issuedToken, setIssuedToken] = useState(null);
  const [draft, setDraft] = useState(null);

  const { data: agents = [], isLoading: agentsLoading } = useQuery({
    queryKey: ['print-agents'],
    queryFn: () => api.printing.listAgents(),
    // "Online" means "asked for work in the last minute and a half", so a
    // stale view here is the difference between diagnosing a dead agent and
    // wondering why nothing prints.
    refetchInterval: 15_000,
  });

  const { data: printers = [] } = useQuery({
    queryKey: ['print-printers'],
    queryFn: () => api.printing.listPrinters(),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['print-agents'] });
    queryClient.invalidateQueries({ queryKey: ['print-printers'] });
    queryClient.invalidateQueries({ queryKey: ['print-status'] });
  };

  const createAgent = useMutation({
    mutationFn: (name) => api.printing.createAgent({ name }),
    onSuccess: (agent) => {
      setNewAgentName('');
      // Held in state and shown until dismissed: this is the only moment the
      // token exists in a readable form anywhere.
      setIssuedToken({ name: agent.name, token: agent.token });
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });

  const rotateToken = useMutation({
    mutationFn: (id) => api.printing.rotateAgentToken(id),
    onSuccess: (agent) => {
      setIssuedToken({ name: agent.name, token: agent.token, rotated: true });
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteAgent = useMutation({
    mutationFn: (id) => api.printing.deleteAgent(id),
    onSuccess: refresh,
    onError: (error) => toast.error(error.message),
  });

  const savePrinter = useMutation({
    mutationFn: (printer) => {
      const payload = {
        name: printer.name,
        kind: printer.kind,
        host: printer.kind === 'ZEBRA_NETWORK' ? printer.host : null,
        port: Number(printer.port) || 9100,
        windowsName: printer.kind === 'WINDOWS_DRIVER' ? printer.windowsName : null,
        agentId: printer.agentId || null,
        isDefault: printer.isDefault,
      };

      return printer.id
        ? api.printing.updatePrinter(printer.id, payload)
        : api.printing.createPrinter(payload);
    },
    onSuccess: () => {
      setDraft(null);
      refresh();
      toast.success('Printer saved');
    },
    onError: (error) => toast.error(error.message),
  });

  const deletePrinter = useMutation({
    mutationFn: (id) => api.printing.deletePrinter(id),
    onSuccess: refresh,
    onError: (error) => toast.error(error.message),
  });

  const copyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success('Token copied');
    } catch {
      toast.error('Could not copy — select the token and copy it by hand.');
    }
  };

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
        <CardTitle className="flex items-center gap-2">
          <PrinterIcon className="w-5 h-5" />
          Printing
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-8">
        <Alert className="border-slate-200 bg-slate-50">
          <Monitor className="h-4 w-4 text-slate-600" />
          <AlertDescription className="text-slate-700">
            Anyone can print a label from the browser, to any printer their PC can reach — that
            needs nothing set up here. This page is for sending labels straight to a printer with
            no dialog, which needs a small program running on a PC in the shop.
          </AlertDescription>
        </Alert>

        {issuedToken && (
          <Alert className="border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900 space-y-3">
              <p className="font-medium">
                {issuedToken.rotated ? 'New token for' : 'Token for'} “{issuedToken.name}”. Copy it
                now — it cannot be shown again, only replaced.
              </p>

              <div className="flex gap-2">
                <code className="flex-1 rounded bg-white px-3 py-2 text-xs break-all border border-amber-200">
                  {issuedToken.token}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyToken(issuedToken.token)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <Button size="sm" variant="ghost" onClick={() => setIssuedToken(null)}>
                I have copied it
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* ---- Agents ---- */}
        <section className="space-y-3">
          <div>
            <h3 className="font-semibold text-slate-900">Shop PCs</h3>
            <p className="text-sm text-slate-500">
              Each runs the print agent. Setup instructions are in the print-agent folder.
            </p>
          </div>

          {agentsLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          ) : agents.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">
              None yet. Add one, then run the agent on that PC with the token it gives you.
            </p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {agents.map((agent) => (
                <li key={agent.id} className="flex items-center gap-3 p-3">
                  {agent.online ? (
                    <Wifi className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <WifiOff className="w-4 h-4 text-slate-400 shrink-0" />
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{agent.name}</p>
                    <p className="text-xs text-slate-500">
                      {agent.online ? 'Running' : lastSeen(agent.lastSeenAt)} · token …
                      {agent.tokenHint}
                      {agent.version ? ` · v${agent.version}` : ''}
                    </p>
                  </div>

                  <Badge variant="outline" className="shrink-0">
                    {agent.printers.length} printer{agent.printers.length === 1 ? '' : 's'}
                  </Badge>

                  <Button
                    size="sm"
                    variant="ghost"
                    title="Issue a new token"
                    onClick={() => rotateToken.mutate(agent.id)}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteAgent.mutate(agent.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2">
            <Input
              value={newAgentName}
              onChange={(event) => setNewAgentName(event.target.value)}
              placeholder="Front counter PC"
              className="max-w-xs"
            />
            <Button
              variant="outline"
              disabled={!newAgentName.trim() || createAgent.isPending}
              onClick={() => createAgent.mutate(newAgentName.trim())}
            >
              {createAgent.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add PC
            </Button>
          </div>
        </section>

        {/* ---- Printers ---- */}
        <section className="space-y-3">
          <div>
            <h3 className="font-semibold text-slate-900">Printers</h3>
            <p className="text-sm text-slate-500">
              A network Zebra takes ZPL labels. Everything else — a DYMO, a USB Zebra, an office
              printer — takes PDFs through its Windows driver.
            </p>
          </div>

          {printers.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">No printers set up.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {printers.map((printer) => (
                <li key={printer.id} className="flex items-center gap-3 p-3">
                  <PrinterIcon className="w-4 h-4 text-slate-400 shrink-0" />

                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">
                      {printer.name}
                      {printer.isDefault && (
                        <Badge variant="secondary" className="ml-2">
                          Default
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {printer.kind === 'ZEBRA_NETWORK'
                        ? `Network Zebra · ${printer.host}:${printer.port} · ZPL`
                        : `Windows printer · ${printer.windowsName} · PDF`}
                      {printer.agent ? ` · ${printer.agent.name}` : ' · no PC assigned'}
                    </p>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setDraft({
                        ...EMPTY_PRINTER,
                        ...printer,
                        host: printer.host ?? '',
                        windowsName: printer.windowsName ?? '',
                        agentId: printer.agentId ?? '',
                      })
                    }
                  >
                    Edit
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deletePrinter.mutate(printer.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {draft ? (
            <PrinterForm
              draft={draft}
              agents={agents}
              onChange={setDraft}
              onCancel={() => setDraft(null)}
              onSave={() => savePrinter.mutate(draft)}
              saving={savePrinter.isPending}
            />
          ) : (
            <Button
              variant="outline"
              disabled={agents.length === 0}
              onClick={() => setDraft({ ...EMPTY_PRINTER, agentId: agents[0]?.id ?? '' })}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add printer
            </Button>
          )}

          {agents.length === 0 && (
            <p className="text-xs text-slate-500">Add a shop PC first — a printer belongs to one.</p>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

function PrinterForm({ draft, agents, onChange, onCancel, onSave, saving }) {
  const set = (field) => (value) => onChange({ ...draft, [field]: value });
  const isZebra = draft.kind === 'ZEBRA_NETWORK';

  return (
    <div className="rounded-lg border p-4 space-y-4 bg-slate-50">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="printer-name">Name</Label>
          <Input
            id="printer-name"
            value={draft.name}
            onChange={(event) => set('name')(event.target.value)}
            placeholder="Back room Zebra"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="printer-kind">Type</Label>
          <Select value={draft.kind} onValueChange={set('kind')}>
            <SelectTrigger id="printer-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="WINDOWS_DRIVER">Windows printer (DYMO, USB, laser)</SelectItem>
              <SelectItem value="ZEBRA_NETWORK">Network Zebra (ZPL over the network)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isZebra ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="printer-host">Address on the shop network</Label>
              <Input
                id="printer-host"
                value={draft.host}
                onChange={(event) => set('host')(event.target.value)}
                placeholder="192.168.1.50"
              />
              <p className="text-xs text-slate-500">
                Printed on the printer&rsquo;s network configuration label.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="printer-port">Port</Label>
              <Input
                id="printer-port"
                type="number"
                value={draft.port}
                onChange={(event) => set('port')(event.target.value)}
              />
              <p className="text-xs text-slate-500">9100 unless it has been changed.</p>
            </div>
          </>
        ) : (
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="printer-windows">Printer name in Windows</Label>
            <Input
              id="printer-windows"
              value={draft.windowsName}
              onChange={(event) => set('windowsName')(event.target.value)}
              placeholder="DYMO LabelWriter 450"
            />
            <p className="text-xs text-slate-500">
              Exactly as it appears in Settings → Printers &amp; scanners on that PC, spacing and
              all.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="printer-agent">Attached to</Label>
          <Select value={draft.agentId} onValueChange={set('agentId')}>
            <SelectTrigger id="printer-agent">
              <SelectValue placeholder="Choose a PC" />
            </SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.isDefault}
              onChange={(event) => set('isDefault')(event.target.checked)}
              className="rounded border-slate-300"
            />
            Offer this one first
          </label>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save printer
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function lastSeen(timestamp) {
  if (!timestamp) return 'Never connected';

  const seconds = Math.round((Date.now() - new Date(timestamp).getTime()) / 1000);

  if (seconds < 120) return 'Last seen just now';
  if (seconds < 3600) return `Last seen ${Math.round(seconds / 60)} minutes ago`;
  if (seconds < 86_400) return `Last seen ${Math.round(seconds / 3600)} hours ago`;

  return `Last seen ${Math.round(seconds / 86_400)} days ago`;
}
